// Route-based tab strip: each tab is its own URL.
//
// Used where several existing screens were gathered under one menu entry
// (admin menu simplification, part 2 — 2026-10-05). Every screen keeps its own
// address, filters and deep links; the strip only lets the operator move
// between the sibling screens. Shared on purpose: any later "several screens,
// one entry" merge reuses this instead of a page-local tab button.

import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { cn } from '../../lib/utils'

export type RouteTab = {
  to: string
  label: string
}

export type RouteTabsProps = {
  title: string
  tabs: RouteTab[]
  activeTo: string
  className?: string
}

const tabClass = (active: boolean) =>
  cn(
    'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:shadow-[var(--shadow-focus)]',
    active
      ? 'border-[var(--color-action)] text-[var(--color-text)]'
      : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
  )

function TabStripFrame({
  title,
  hideTitle = false,
  className,
  children,
}: {
  title: string
  hideTitle?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <nav
      aria-label={title}
      data-testid="route-tabs"
      className={cn('mb-4 border-b border-[var(--color-border)]', className)}
    >
      {!hideTitle && (
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {title}
        </div>
      )}
      <div className="-mb-px flex gap-1 overflow-x-auto" role="tablist">
        {children}
      </div>
    </nav>
  )
}

export function RouteTabs({ title, tabs, activeTo, className }: RouteTabsProps) {
  return (
    <TabStripFrame title={title} className={className}>
      {tabs.map((tab) => {
        const active = tab.to === activeTo
        return (
          <Link
            key={tab.to}
            to={tab.to}
            role="tab"
            aria-selected={active}
            aria-current={active ? 'page' : undefined}
            className={tabClass(active)}
          >
            {tab.label}
          </Link>
        )
      })}
    </TabStripFrame>
  )
}

// Same strip for screens that switch views in place rather than by URL — e.g.
// the admin Settings shell, which tracks its own active section (2026-10-06).
// `hideTitle`: the page's own heading already names the strip (Pricing,
// 2026-10-15) — the title then only labels the nav for screen readers.
export type SectionTab = { id: string; label: string }

export function SectionTabs({
  title,
  tabs,
  hideTitle,
  activeId,
  onSelect,
  className,
}: {
  title: string
  hideTitle?: boolean
  tabs: SectionTab[]
  activeId: string
  onSelect: (id: string) => void
  className?: string
}) {
  // On a narrow screen the strip scrolls sideways; keep the open tab in view
  // (e.g. a page that opens on its last tab).
  const activeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [activeId])

  return (
    <TabStripFrame title={title} hideTitle={hideTitle} className={className}>
      {tabs.map((tab) => {
        const active = tab.id === activeId
        return (
          <button
            key={tab.id}
            ref={active ? activeRef : undefined}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(tab.id)}
            className={tabClass(active)}
          >
            {tab.label}
          </button>
        )
      })}
    </TabStripFrame>
  )
}
